import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Box, Alert, CircularProgress, Chip,
} from '@mui/material';
import {
    useRodinnyPinStatusQuery,
    useGenerateRodinnyPinMutation,
    useInvalidateRodinnyPinMutation,
} from '../api/JsdhNotifikaceService/service/ExterniKontaktyMutations';

interface Props {
    open: boolean;
    onClose: () => void;
    /** undefined = self-service (vlastni PIN), jinak admin sprava PINu daneho clena */
    userId?: number;
    userJmeno?: string;
}

const RodinnyPristupDialog: React.FC<Props> = ({ open, onClose, userId, userJmeno }) => {
    const { data: status, refetch } = useRodinnyPinStatusQuery(userId, { enabled: open });
    const generateMutation = useGenerateRodinnyPinMutation(userId);
    const invalidateMutation = useInvalidateRodinnyPinMutation(userId);
    const [vygenerovanyPin, setVygenerovanyPin] = useState<string | null>(null);

    const handleGenerate = () => {
        generateMutation.mutate(undefined, {
            onSuccess: (data) => {
                setVygenerovanyPin(data.pin);
                refetch();
            },
        });
    };

    const handleInvalidate = () => {
        invalidateMutation.mutate(undefined, {
            onSuccess: () => {
                setVygenerovanyPin(null);
                refetch();
            },
        });
    };

    const handleClose = () => {
        setVygenerovanyPin(null);   // plaintext nikdy nedrzet po zavreni
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>Rodinný přístup{userJmeno ? ` — ${userJmeno}` : ''}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    PIN slouží rodině pro přihlášení v mobilní aplikaci (kód jednotky + telefon člena + PIN).
                    Rodina pak dostává oznámení „člen vyjel k zásahu".
                </Typography>

                {status?.aktivni && !vygenerovanyPin && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Aktivní PIN existuje{status.createdAt
                            ? ` (od ${new Date(status.createdAt).toLocaleDateString('cs-CZ')})` : ''}.
                        Vygenerováním nového se starý zneplatní.
                    </Alert>
                )}

                {vygenerovanyPin && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        <Typography variant="body2" gutterBottom>
                            Nový PIN (zobrazí se jen teď — předejte rodině):
                        </Typography>
                        <Chip label={vygenerovanyPin} color="success"
                              sx={{ fontSize: '1.4rem', fontWeight: 700, letterSpacing: 4, p: 2 }} />
                    </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" onClick={handleGenerate} disabled={generateMutation.isPending}>
                        {generateMutation.isPending && <CircularProgress size={18} sx={{ mr: 1 }} />}
                        {status?.aktivni ? 'Vygenerovat nový PIN' : 'Vygenerovat PIN'}
                    </Button>
                    {status?.aktivni && (
                        <Button color="error" onClick={handleInvalidate} disabled={invalidateMutation.isPending}>
                            Zneplatnit
                        </Button>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Zavřít</Button>
            </DialogActions>
        </Dialog>
    );
};

export default RodinnyPristupDialog;
